import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GrassIcon from "@mui/icons-material/Grass";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import type { VirtualItem } from "@tanstack/react-virtual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNotification } from "app/NotificationProvider";
import EventPhotos from "common/components/EventPhotos";
import { SuccessSparkle } from "common/components/SuccessSparkle";
import FeedingIcon from "common/icons/FeedingIcon";
import FertiliserIcon from "common/icons/FertiliserIcon";
import HarvestIcon from "common/icons/HarvestIcon";
import OtherEventIcon from "common/icons/OtherEventIcon";
import PurchaseIcon from "common/icons/PurchaseIcon";
import SeedingIcon from "common/icons/SeedingIcon";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import { M2_PER_HECTARE } from "common/utils/area";
import { dmUtilisedTotalKg, LEAF_ZONES } from "common/utils/grazing-logic";
import { useIsMobile } from "common/utils/Hooks";
import { useOnlineStatus } from "common/utils/useOnlineStatus";
import { buildReadingPayload } from "features/animalGroupDetail/RotationSeasonGrid";
import { convertToFeedStockAmountString } from "features/feedPurchaseDetail/generalMethods";
import UpdateFeedEvent from "features/feedPurchaseDetail/UpdateFeedEvent";
import UpdateFeedPurchaseEvent from "features/feedPurchaseDetail/UpdateFeedPurchaseEvent";
import {
  convertToFertilizerAmountString,
  getFertilizerTypeString,
} from "features/fertilizerInput/FertilizerEventListItem";
import UpdateFertilizerEvent from "features/fertilizerInput/UpdateFertilizerEvent";
import UpdateFieldPreparationEvent from "features/fieldPreparationInput/UpdateFieldPreparationEvent";
import UpdateReseedEvent from "features/singleReseedEventInput/UpdateReseedEvent";
import { convertToYieldAmountString } from "features/yieldInput/generalMethods";
import UpdateYieldEvent from "features/yieldInput/UpdateYieldEvent";
import {
  FertilizerEvent,
  FieldPreparationEvent,
  GeneralFeedEvent,
  GeneralFeedPurchaseEvent,
  MoveEvent,
  ReseedEvent,
  SOLD_OR_KEPT,
  useArableFields,
  useCurrentFarmAnimalGroups,
  useDeleteFeedEvent,
  useDeleteFeedPurchaseEvent,
  useDeleteFertilizerEvent,
  useDeleteFieldPreparationEvent,
  useDeleteReseedEvent,
  useDeleteYieldEvent,
  useFeedEvents,
  useFeedPurchaseEvents,
  useFertilizerEvents,
  useFieldPreparationEvents,
  useFields,
  useFieldsByIds,
  useReseedEvents,
  useYieldEvents,
  YIELD_TYPES,
  YieldEvent,
} from "model";
import { EventType } from "model/readingsApiHelpers";
import {
  useCreateReading,
  useDeleteReading,
  useUpdateReading,
} from "model/useReadingMutations";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppStore } from "stores";
import {
  buildCsvContent,
  buildPdfBody,
  filterEvents,
  formatDateRangeLabel,
  sortEventsByDateDesc,
} from "./recordsUtils";

// ─── Shared styles ────────────────────────────────────────────────────────────
const exportBtnSx = {
  height: "32px",
  fontSize: "14px",
  fontWeight: 500,
  textTransform: "none" as const,
  backgroundColor: "#ffffff",
  borderColor: "#e4e4e7",
  color: "#18181b",
  borderRadius: "6px",
  padding: "8px 12px",
  fontFamily: "Inter, sans-serif",
  "&:hover": { backgroundColor: "#fafafa", borderColor: "#e4e4e7" },
};

const iconColor = "#71717a";

const thSx = {
  fontWeight: 600,
  fontSize: 12,
  color: "#6b7280",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  py: 1,
  px: 1.5,
};

const tdSx = {
  fontSize: 13,
  color: "#18181b",
  py: 1.25,
  px: 1.5,
};

// Grid column widths shared between header and rows.
// Desktop includes a Summary column (the trailing 1fr before the chevron);
// mobile drops it — the summary is never readable in a narrow row, so the
// user taps the row to expand and read the details there instead.
const GRID_COLS = "40px 90px 110px minmax(100px,200px) 1fr 40px";
const GRID_COLS_MOBILE = "40px 90px 110px 1fr 40px";

// ─── Types ────────────────────────────────────────────────────────────────────
type GrazingMetrics = {
  guid: string;
  fieldGuid: string;
  animalGroupGuid?: string | null;
  kind?: "user" | "machine";
  verificationStatus?: "Proposed" | "Accepted" | "Rejected";
  animalGroup: string;
  moveIn: string;
  moveOut: string;
  dateTimeIn: string; // ISO string for API operations
  dateTimeOut?: string; // ISO string for API operations
  startDryMatter: number; // kg DM/ha before grazing
  endDryMatter: number; // kg DM/ha after grazing
  postTarget: number; // kg DM/ha target (should not graze below this)
  fieldArea: number; // hectares
  moveOutMissing: boolean;
};

type UnifiedEventSource =
  | { type: "reseed"; event: ReseedEvent }
  | { type: "feed"; event: GeneralFeedEvent }
  | { type: "feedPurchase"; event: GeneralFeedPurchaseEvent }
  | { type: "yield"; event: YieldEvent }
  | { type: "fertilizer"; event: FertilizerEvent }
  | { type: "fieldPreparation"; event: FieldPreparationEvent };

type UnifiedEvent = {
  id: string;
  date: string;
  type:
    | "seeding"
    | "fertiliser"
    | "harvest"
    | "feeding"
    | "purchase"
    | "fieldPrep"
    | "grazing"
    | "grazing-proposed";
  fields: string[];
  summary: string;
  details: Array<{ label: string; value: string }>;
  grazingMetrics?: GrazingMetrics;
  source?: UnifiedEventSource;
};

function isoToDMY(isoDate: string): string {
  const [y, m, d] = isoDate.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

function useUnifiedEvents(): {
  events: UnifiedEvent[];
  allFieldNames: string[];
} {
  const { t } = useTranslation();
  const locale = useAppStore((state) => state.locale);
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const { data: grassFields = [] } = useFields();
  const { data: arableFields = [] } = useArableFields();

  const fieldByGuid = useMemo(() => {
    const map = new Map<string, { name: string; area: number }>();
    [...grassFields, ...arableFields].forEach((f) =>
      map.set(f.guid, { name: f.name, area: f.area })
    );
    return map;
  }, [grassFields, arableFields]);

  const { data: reseedEvents = [] } = useReseedEvents(
    currentFarmId ?? undefined
  );
  const { data: feedEvents = [] } = useFeedEvents(currentFarmId ?? undefined);
  const { data: feedPurchaseEvents = [] } = useFeedPurchaseEvents(
    currentFarmId ?? undefined
  );
  const { data: yieldEvents = [] } = useYieldEvents(currentFarmId ?? undefined);
  const { data: fertilizerEvents = [] } = useFertilizerEvents(
    currentFarmId ?? undefined
  );
  const { data: fieldPreparationEvents = [] } = useFieldPreparationEvents(
    currentFarmId ?? undefined
  );

  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const online = useOnlineStatus();

  const moveEventResults = useQueries({
    queries: animalGroups.map((ag) => ({
      queryKey: queryKeys.animalGroups.moveEvents(ag.guid),
      queryFn: async () => {
        const res = await fetch(
          `${import.meta.env.VITE_API_ENDPOINT}/v3/animalGroups/${
            ag.guid
          }/moveEvents`,
          {
            headers: {
              Authorization: `Bearer ${
                window.localStorage.getItem("jwt") || ""
              }`,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch move events");
        const events = (await res.json()) as MoveEvent[];
        return events.filter((ev) => ev.verificationStatus !== "Rejected");
      },
      enabled: online && animalGroups.length > 0,
    })),
  });

  const events = useMemo(() => {
    const resolveFieldNames = (guids: string[]) =>
      guids
        .map((g) => fieldByGuid.get(g)?.name)
        .filter((n): n is string => Boolean(n));

    const all: UnifiedEvent[] = [];

    for (const ev of reseedEvents) {
      const grassType = t(`fieldinput.grassType.option.${ev.newGrassType}`);
      const seedingMethod = ev.seedingMethod
        ? t(`reseedEvent.seedingMethod.option.${ev.seedingMethod}`)
        : "";
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.reseedDate),
        type: "seeding",
        fields: resolveFieldNames(ev.fieldGuids),
        summary: [grassType, seedingMethod].filter(Boolean).join(" · "),
        details: [
          { label: "New grass type", value: grassType },
          ...(seedingMethod
            ? [{ label: "Seeding method", value: seedingMethod }]
            : []),
          ...(ev.productCost != null
            ? [
                {
                  label: "Cost",
                  value: `£${ev.productCost.toLocaleString(locale)}`,
                },
              ]
            : []),
        ],
        source: { type: "reseed", event: ev },
      });
    }

    for (const ev of fertilizerEvents) {
      const fertType = getFertilizerTypeString(ev, t);
      const amount = convertToFertilizerAmountString(
        ev.totalAmount,
        locale,
        ev.totalAmountUnit,
        t
      );
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.fertilizerApplicationDate),
        type: "fertiliser",
        fields: resolveFieldNames(ev.fieldGuids),
        summary: `${fertType} · ${amount}`,
        details: [
          { label: "Type", value: fertType },
          { label: "Amount", value: amount },
          ...(ev.productCost != null
            ? [
                {
                  label: "Cost",
                  value: `£${ev.productCost.toLocaleString(locale)}`,
                },
              ]
            : []),
        ],
        source: { type: "fertilizer", event: ev },
      });
    }

    for (const ev of yieldEvents) {
      const yieldType = t(
        `yieldEvent.yieldType.option.${ev.yieldDetails.yieldType}`
      );
      let fields: string[] = [];
      if (
        ev.yieldDetails.yieldType === YIELD_TYPES.GRASS ||
        ev.yieldDetails.yieldType === YIELD_TYPES.CROP
      ) {
        fields = resolveFieldNames(ev.yieldDetails.fieldGuids);
      }
      const amounts = ev.yieldDetails.yieldAmounts.map((ya) => {
        const amtStr = convertToYieldAmountString(
          ya.amount,
          locale,
          ya.unitType,
          t
        );
        let typeDetail = "";
        if ("grassYieldType" in ya)
          typeDetail = t(`yieldEvent.yieldType.option.${ya.grassYieldType}`);
        else if ("cropYieldType" in ya)
          typeDetail = t(`yieldEvent.yieldType.option.${ya.cropYieldType}`);
        const suffix =
          ya.soldOrKept === SOLD_OR_KEPT.KEPT
            ? t("yieldEvent.amount.kept", { amount: amtStr })
            : t("yieldEvent.amount.sold", {
                amount: amtStr,
                soldPrice: ya.revenue
                  ? `${ya.revenue.toFixed(2)} £`
                  : "unknown revenue",
              });
        return typeDetail ? `${typeDetail} — ${suffix}` : suffix;
      });
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.yieldEventDate),
        type: "harvest",
        fields,
        summary: `${yieldType} yield · ${amounts.join("; ")}`,
        details: [
          { label: "Type", value: `${yieldType} yield` },
          { label: "Yield amounts", value: amounts.join("; ") },
        ],
        source: { type: "yield", event: ev },
      });
    }

    for (const ev of feedEvents) {
      const feedItems = ev.feed.map((f) => {
        const feedType = t(`feedPurchase.feedType.option.${f.feedType}`);
        const amount = convertToFeedStockAmountString(
          f.amount,
          locale,
          f.unitType,
          t
        );
        return `${feedType}: ${amount}`;
      });
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.feedEventDate),
        type: "feeding",
        fields: [],
        summary: feedItems.join(", "),
        details: feedItems.map((item, i) => ({
          label: `Feed ${i + 1}`,
          value: item,
        })),
        source: { type: "feed", event: ev },
      });
    }

    for (const ev of feedPurchaseEvents) {
      const items = ev.feedPurchases.map((p) => {
        const feedType = t(`feedPurchase.feedType.option.${p.feedType}`);
        const amount = convertToFeedStockAmountString(
          p.amount,
          locale,
          p.unitType,
          t
        );
        return `${feedType}: ${amount}`;
      });
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.feedPurchaseEventDate),
        type: "purchase",
        fields: [],
        summary: items.join("; "),
        details: items.map((item, i) => ({
          label: `Purchase ${i + 1}`,
          value: item,
        })),
        source: { type: "feedPurchase", event: ev },
      });
    }

    for (const ev of fieldPreparationEvents) {
      // fuel/machinery are optional now — "Other" events reuse this same
      // type with neither set, only `note`.
      const machineryStr = (ev.machinery ?? [])
        .map((m) => t(`yieldEventInput.machinery.options.${m}`))
        .join(", ");
      const summaryParts = [
        ev.fuel != null ? `${ev.fuel} L fuel` : "",
        machineryStr,
        ev.note ?? "",
      ].filter(Boolean);
      all.push({
        id: ev.guid,
        date: isoToDMY(ev.fieldPreparationEventDate),
        type: "fieldPrep",
        fields: resolveFieldNames(ev.fieldGuids),
        summary: summaryParts.join(" · "),
        details: [
          ...(ev.fuel != null
            ? [{ label: "Fuel", value: `${ev.fuel} litre` }]
            : []),
          ...(machineryStr
            ? [{ label: "Machinery", value: machineryStr }]
            : []),
          ...(ev.note ? [{ label: "Notes", value: ev.note }] : []),
        ],
        source: { type: "fieldPreparation", event: ev },
      });
    }

    for (let i = 0; i < animalGroups.length; i++) {
      const ag = animalGroups[i];
      const moveEvents = moveEventResults[i]?.data ?? [];
      for (const ev of moveEvents) {
        if (!ev.dateTimeIn) continue;
        const dateStr = isoToDMY(ev.dateTimeIn);
        const moveOutStr = ev.dateTimeOut ? isoToDMY(ev.dateTimeOut) : "";
        const fieldInfo = ev.fieldGuid
          ? fieldByGuid.get(ev.fieldGuid)
          : undefined;
        const fieldName = ev.fieldName ?? fieldInfo?.name ?? "Unknown field";
        const fieldArea = fieldInfo ? fieldInfo.area / M2_PER_HECTARE : 1;
        const utilized =
          ev.dateTimeOut && ev.startDryMatter != null && ev.endDryMatter != null
            ? dmUtilisedTotalKg(ev.startDryMatter, ev.endDryMatter, fieldArea)
            : null;
        const isProposed =
          ev.kind === "machine" && ev.verificationStatus === "Proposed";
        all.push({
          id: ev.guid,
          date: dateStr,
          type: isProposed ? "grazing-proposed" : "grazing",
          fields: [fieldName],
          summary: [
            ag.name,
            `${dateStr.slice(0, 5)} → ${
              moveOutStr ? moveOutStr.slice(0, 5) : "?"
            }`,
            utilized != null
              ? `${utilized.toLocaleString()} kg DM utilized`
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
          details: [],
          grazingMetrics: {
            guid: ev.guid,
            fieldGuid: ev.fieldGuid ?? "",
            animalGroupGuid: ag.guid,
            kind: ev.kind,
            verificationStatus: ev.verificationStatus,
            animalGroup: ag.name,
            moveIn: dateStr,
            moveOut: moveOutStr,
            dateTimeIn: ev.dateTimeIn,
            dateTimeOut: ev.dateTimeOut ?? undefined,
            startDryMatter: ev.startDryMatter ?? 0,
            endDryMatter: ev.endDryMatter ?? 0,
            postTarget: 1500,
            fieldArea,
            moveOutMissing:
              !ev.dateTimeOut &&
              (moveEvents.some(
                (other) =>
                  other.dateTimeIn &&
                  ev.dateTimeIn &&
                  other.dateTimeIn > ev.dateTimeIn
              ) ||
                (Date.now() - new Date(ev.dateTimeIn).getTime()) / 86_400_000 >
                  365),
          },
        });
      }
    }

    return sortEventsByDateDesc(all);
  }, [
    reseedEvents,
    feedEvents,
    feedPurchaseEvents,
    yieldEvents,
    fertilizerEvents,
    fieldPreparationEvents,
    animalGroups,
    moveEventResults,
    fieldByGuid,
    t,
    locale,
  ]);

  const allFieldNames = useMemo(
    () =>
      [
        ...new Set([
          ...grassFields.map((f) => f.name),
          ...arableFields.map((f) => f.name),
        ]),
      ].sort(),
    [grassFields, arableFields]
  );

  return { events, allFieldNames };
}

export const TYPE_LABELS: Record<UnifiedEvent["type"], string> = {
  seeding: "Seeding",
  fertiliser: "Fertiliser",
  harvest: "Harvest",
  feeding: "Feeding",
  purchase: "Purchase",
  fieldPrep: "Other",
  grazing: "Grazing",
  "grazing-proposed": "Proposed grazing",
};

// ─── Shared sub-components ────────────────────────────────────────────────────

export function EventTypeIcon({
  type,
  size = 20,
}: {
  type: UnifiedEvent["type"];
  size?: number;
}) {
  const sx = { width: size, height: size, color: iconColor };
  switch (type) {
    case "seeding":
      return <SeedingIcon sx={sx} />;
    case "fertiliser":
      return <FertiliserIcon sx={sx} />;
    case "harvest":
      return <HarvestIcon sx={sx} />;
    case "feeding":
      return <FeedingIcon sx={sx} />;
    case "purchase":
      return <PurchaseIcon sx={sx} />;
    case "fieldPrep":
      return <OtherEventIcon sx={sx} />;
    case "grazing":
      return <GrassIcon sx={{ ...sx, color: "#16a34a" }} />;
    case "grazing-proposed":
      return <SatelliteAltIcon sx={{ ...sx, color: "#7D5DA3" }} />;
  }
}

export function TypeBadge({ type }: { type: UnifiedEvent["type"] }) {
  const isGrazing = type === "grazing";
  const isProposed = type === "grazing-proposed";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        fontSize: 12,
        fontWeight: 500,
        backgroundColor: isGrazing
          ? "#f0fdf4"
          : isProposed
          ? "#F1EFF7"
          : "#f4f4f5",
        color: isGrazing ? "#15803d" : isProposed ? "#7D5DA3" : "#52525b",
        border: `1px solid ${
          isGrazing ? "#bbf7d0" : isProposed ? "#D3C9E7" : "#e4e4e7"
        }`,
        whiteSpace: "nowrap",
      }}
    >
      {isProposed ? "Grazing" : TYPE_LABELS[type]}
    </Box>
  );
}

const ZONE_MAX = 3000;
const BAR_H = 210;
const COLLAPSED_ROW_ESTIMATE = 53;
const EXPANDED_GRAZING_ROW_ESTIMATE = 287;
const EXPANDED_DETAIL_ROW_ESTIMATE = 150;

export function estimatedUnifiedRowSize(
  event: Pick<UnifiedEvent, "grazingMetrics"> | undefined,
  expanded: boolean
): number {
  if (!event || !expanded) return COLLAPSED_ROW_ESTIMATE;
  return event.grazingMetrics
    ? EXPANDED_GRAZING_ROW_ESTIMATE
    : EXPANDED_DETAIL_ROW_ESTIMATE;
}
// Fixed agronomic zones (kg DM/ha, bottom to top) derived from the shared
// leaf-stage zones; the top zone's open-ended max is capped at ZONE_MAX so the
// bar chart has a finite height for it.
const GRASS_ZONES = LEAF_ZONES.map((z) => ({
  min: z.min,
  max: Number.isFinite(z.max) ? z.max : ZONE_MAX,
  label: z.label,
  bg: z.bg,
  tc: z.tc,
  desc: z.desc,
}));

// ─── Grazing detail panel — grass zone bar ────────────────────────────────────
function GrazingDetail({
  metrics: initialMetrics,
  fields,
  onDeleted,
  onAccepted,
  showAcceptedAnimation = false,
}: {
  metrics: GrazingMetrics;
  fields: string[];
  onDeleted?: () => void;
  onAccepted?: (acceptedId: string) => void;
  showAcceptedAnimation?: boolean;
}) {
  const [metrics, setMetrics] = useState(initialMetrics);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(initialMetrics);
  const isMobile = useIsMobile();

  const queryClient = useQueryClient();
  const updateReading = useUpdateReading();
  const createReading = useCreateReading();
  const deleteReading = useDeleteReading();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const d900 = "#1C4321";
  const d700 = "#2E7831";
  const daisy700 = "#A48137";
  const daisy100 = "#FAF1B6";
  const daisy300 = "#F7E880";

  const isMachineProposed =
    metrics.kind === "machine" && metrics.verificationStatus === "Proposed";
  const missingPreCover = !metrics.startDryMatter;
  const grassPerHa = metrics.startDryMatter - metrics.endDryMatter;
  const totalKgDM = Math.round(grassPerHa * metrics.fieldArea);
  const isOvergrazed = metrics.endDryMatter < metrics.postTarget;
  const overgraze = isOvergrazed
    ? Math.round(
        (metrics.postTarget - metrics.endDryMatter) * metrics.fieldArea
      )
    : 0;
  const grazingInProgress = !metrics.dateTimeOut && !metrics.moveOutMissing;

  const fromTop = (v: number) => (1 - Math.min(v, ZONE_MAX) / ZONE_MAX) * BAR_H;
  const fromBot = (v: number) => `${(Math.min(v, ZONE_MAX) / ZONE_MAX) * 100}%`;
  const zoneHPx = (z: typeof GRASS_ZONES[number]) =>
    ((z.max - z.min) / ZONE_MAX) * BAR_H;

  const preLinePx = fromTop(metrics.startDryMatter);
  const postLinePx = fromTop(metrics.endDryMatter);

  const LABEL_H = 26;
  const GAP = 4;
  let preLabelY = preLinePx - 7;
  let postLabelY = postLinePx - 7;
  const deficit = preLabelY + LABEL_H + GAP - postLabelY;
  if (deficit > 0) {
    preLabelY -= deficit / 2;
    postLabelY += deficit / 2;
  }
  preLabelY = Math.max(0, Math.min(BAR_H - LABEL_H * 2 - GAP, preLabelY));
  postLabelY = Math.max(
    preLabelY + LABEL_H + GAP,
    Math.min(BAR_H - LABEL_H, postLabelY)
  );

  const fieldSx = {
    "& .MuiInputBase-input": { fontSize: 13 },
    "& .MuiInputLabel-root": { fontSize: 13 },
  };

  const invalidateMoveEvents = () =>
    void queryClient.invalidateQueries({
      queryKey: ["animalGroups", "moveEvents"],
    });

  const handleSave = () => {
    const ev = {
      guid: metrics.guid,
      fieldGuid: metrics.fieldGuid,
      animalGroupGuid: metrics.animalGroupGuid ?? null,
      kind: metrics.kind ?? "user",
      verificationStatus: metrics.verificationStatus ?? "Accepted",
      dateTimeIn: metrics.dateTimeIn,
      dateTimeOut: metrics.dateTimeOut ?? null,
      startDryMatter: metrics.startDryMatter,
      endDryMatter: metrics.endDryMatter,
      movedToShed: false,
      moveNumber: 0,
      fieldName: null,
    } as const;

    if (isMachineProposed) {
      // Tombstone passes the existing animal group (which may be null) so
      // the rejection does not attach an otherwise-unattached row as a
      // side effect.
      updateReading.mutate(
        buildReadingPayload(
          ev,
          metrics.fieldGuid,
          metrics.animalGroupGuid ?? null,
          {
            verificationStatus: "Rejected",
          }
        ),
        {
          onSuccess: () => {
            createReading.mutate(
              {
                guid: "new",
                manualDryMatterInput: {
                  field: metrics.fieldGuid,
                  dateTimeMeasurementStart: draft.dateTimeIn,
                  dateTimeMeasurementEnd: draft.dateTimeOut ?? draft.dateTimeIn,
                  event: {
                    eventType: EventType.GrazingEvent,
                    dateTimeIn: draft.dateTimeIn,
                    dateTimeOut: draft.dateTimeOut ?? undefined,
                    preEventCover: draft.startDryMatter,
                    postEventCover: draft.endDryMatter,
                    animalGroupGuid: metrics.animalGroupGuid ?? "",
                  },
                  kind: "machine",
                  verificationStatus: "Accepted",
                },
              },
              {
                onSuccess: (acceptedReading) => {
                  setIsEditing(false);
                  invalidateMoveEvents();
                  onAccepted?.(acceptedReading.guid);
                  notify(t("fieldevents.notify.accepted"));
                },
              }
            );
          },
        }
      );
    } else {
      updateReading.mutate(
        buildReadingPayload(
          ev,
          metrics.fieldGuid,
          metrics.animalGroupGuid ?? "",
          {
            dateTimeIn: draft.dateTimeIn,
            dateTimeOut: draft.dateTimeOut ?? null,
            startDryMatter: draft.startDryMatter,
            endDryMatter: draft.endDryMatter,
          }
        ),
        {
          onSuccess: () => {
            setMetrics(draft);
            setIsEditing(false);
            invalidateMoveEvents();
          },
        }
      );
    }
  };

  const handleDelete = () => {
    if (isMachineProposed) {
      // Soft-delete: mark as Rejected so the pipeline won't re-propose the slot.
      const moveEventLike = {
        guid: metrics.guid,
        fieldGuid: metrics.fieldGuid,
        animalGroupGuid: metrics.animalGroupGuid ?? null,
        kind: metrics.kind ?? "user",
        verificationStatus: metrics.verificationStatus ?? "Accepted",
        dateTimeIn: metrics.dateTimeIn,
        dateTimeOut: metrics.dateTimeOut ?? null,
        startDryMatter: metrics.startDryMatter,
        endDryMatter: metrics.endDryMatter,
        movedToShed: false,
        moveNumber: 0,
        fieldName: null,
      } as const;
      // Tombstone passes the existing animal group (which may be null) so
      // the rejection does not attach an otherwise-unattached row as a
      // side effect.
      updateReading.mutate(
        buildReadingPayload(
          moveEventLike,
          metrics.fieldGuid,
          metrics.animalGroupGuid ?? null,
          {
            verificationStatus: "Rejected",
          }
        ),
        {
          onSuccess: () => {
            invalidateMoveEvents();
            onDeleted?.();
          },
        }
      );
    } else {
      deleteReading.mutate(
        {
          readingId: metrics.guid,
          fieldId: metrics.fieldGuid,
          isGrazing: true,
          animalGroupGuid: metrics.animalGroupGuid ?? undefined,
        },
        {
          onSuccess: () => {
            invalidateMoveEvents();
            onDeleted?.();
          },
        }
      );
    }
  };

  const handleAccept = () => {
    const ev = {
      guid: metrics.guid,
      fieldGuid: metrics.fieldGuid,
      animalGroupGuid: metrics.animalGroupGuid ?? null,
      kind: metrics.kind ?? "user",
      verificationStatus: metrics.verificationStatus ?? "Accepted",
      dateTimeIn: metrics.dateTimeIn,
      dateTimeOut: metrics.dateTimeOut ?? null,
      startDryMatter: metrics.startDryMatter,
      endDryMatter: metrics.endDryMatter,
      movedToShed: false,
      moveNumber: 0,
      fieldName: null,
    } as const;
    updateReading.mutate(
      buildReadingPayload(
        ev,
        metrics.fieldGuid,
        metrics.animalGroupGuid ?? null,
        {
          verificationStatus: "Rejected",
        }
      ),
      {
        onSuccess: () => {
          createReading.mutate(
            {
              guid: "new",
              manualDryMatterInput: {
                field: metrics.fieldGuid,
                dateTimeMeasurementStart: metrics.dateTimeIn,
                dateTimeMeasurementEnd:
                  metrics.dateTimeOut ?? metrics.dateTimeIn,
                event: {
                  eventType: EventType.GrazingEvent,
                  dateTimeIn: metrics.dateTimeIn,
                  dateTimeOut: metrics.dateTimeOut ?? undefined,
                  preEventCover: metrics.startDryMatter,
                  postEventCover: metrics.endDryMatter,
                  animalGroupGuid: metrics.animalGroupGuid ?? "",
                },
                kind: "machine",
                verificationStatus: "Accepted",
              },
            },
            {
              onSuccess: (acceptedReading) => {
                invalidateMoveEvents();
                onAccepted?.(acceptedReading.guid);
                notify(t("fieldevents.notify.accepted"));
              },
            }
          );
        },
      }
    );
  };

  if (isEditing) {
    return (
      <Box
        sx={{ px: 3, py: 2, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            maxWidth: 480,
          }}
        >
          <TextField
            label="Move in"
            type="date"
            size="small"
            value={draft.dateTimeIn.split("T")[0]}
            onChange={(e) => {
              const time = draft.dateTimeIn.split("T")[1] ?? "00:00:00.000Z";
              setDraft({ ...draft, dateTimeIn: `${e.target.value}T${time}` });
            }}
            sx={fieldSx}
          />
          <TextField
            label="Move out"
            type="date"
            size="small"
            value={draft.dateTimeOut?.split("T")[0] ?? ""}
            onChange={(e) => {
              const time =
                (draft.dateTimeOut ?? draft.dateTimeIn).split("T")[1] ??
                "00:00:00.000Z";
              setDraft({
                ...draft,
                dateTimeOut: e.target.value
                  ? `${e.target.value}T${time}`
                  : undefined,
              });
            }}
            sx={fieldSx}
          />
          <TextField
            label="Pre-grazing cover (kg DM/ha)"
            type="number"
            size="small"
            value={draft.startDryMatter}
            onChange={(e) =>
              setDraft({ ...draft, startDryMatter: +e.target.value })
            }
            sx={fieldSx}
          />
          <TextField
            label="Post-grazing cover (kg DM/ha)"
            type="number"
            size="small"
            value={draft.endDryMatter}
            onChange={(e) =>
              setDraft({ ...draft, endDryMatter: +e.target.value })
            }
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={updateReading.isPending || createReading.isPending}
            sx={{
              textTransform: "none",
              fontSize: 13,
              backgroundColor: isMachineProposed ? "#7D5DA3" : d900,
              "&:hover": {
                backgroundColor: isMachineProposed ? "#433765" : d700,
              },
            }}
          >
            {isMachineProposed ? "Accept" : "Save"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDraft(metrics);
              setIsEditing(false);
            }}
            sx={{
              textTransform: "none",
              fontSize: 13,
              borderColor: "#e4e4e7",
              color: "#374151",
            }}
          >
            Cancel
          </Button>
          {isMachineProposed && (
            <Typography sx={{ fontSize: 11, color: "#9ca3af", ml: 0.5 }}>
              Edits will be saved as an accepted event
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: isMobile ? 2 : 3,
        py: 1.5,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 2 : 3,
        alignItems: isMobile ? "stretch" : "flex-start",
      }}
    >
      {/* Chart group: zone labels + bar + pre/post labels */}
      <Box
        sx={{
          display: "flex",
          gap: isMobile ? 2 : 3,
          alignItems: "flex-start",
          justifyContent: isMobile ? "center" : "flex-start",
          flexShrink: 0,
        }}
      >
        {/* Zone labels */}
        <Box
          sx={{
            position: "relative",
            width: 100,
            height: BAR_H,
            flexShrink: 0,
          }}
        >
          {GRASS_ZONES.map((zone) => {
            const midV = (zone.min + zone.max) / 2;
            const topY = fromTop(midV) - 6;
            const hPx = zoneHPx(zone);
            if (hPx < 14) return null; // skip label for very thin zones
            return (
              <Typography
                key={zone.min}
                sx={{
                  position: "absolute",
                  top: Math.max(0, Math.min(BAR_H - 12, topY)),
                  right: 0,
                  fontSize: 10,
                  fontWeight: 500,
                  color: zone.tc,
                  lineHeight: 1.2,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {zone.label}
              </Typography>
            );
          })}
        </Box>

        {/* The bar — overflow visible so Tooltips can escape; rounded corners applied per first/last zone */}
        <Box
          sx={{ position: "relative", width: 44, height: BAR_H, flexShrink: 0 }}
        >
          {GRASS_ZONES.map((zone, i) => {
            const isFirst = i === 0;
            const isLast = i === GRASS_ZONES.length - 1;
            return (
              <Tooltip
                key={zone.min}
                title={
                  <Box sx={{ p: 0.25 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                      {zone.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}
                    >
                      {zone.min.toLocaleString()}–{zone.max.toLocaleString()} kg
                      DM/ha
                    </Typography>
                    <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                      {zone.desc}
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: fromBot(zone.min),
                    left: 0,
                    right: 0,
                    height: `${((zone.max - zone.min) / ZONE_MAX) * 100}%`,
                    backgroundColor: zone.bg,
                    borderRadius: isFirst
                      ? "0 0 6px 6px"
                      : isLast
                      ? "6px 6px 0 0"
                      : 0,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            );
          })}
          {/* Pre-grazing cover line */}
          {!missingPreCover && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: fromBot(metrics.startDryMatter),
                height: 3,
                backgroundColor: d900,
                zIndex: 1,
              }}
            />
          )}
          {/* Post-grazing cover line — only when grazing is complete */}
          {metrics.dateTimeOut && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: fromBot(metrics.endDryMatter),
                height: 2,
                backgroundColor: isOvergrazed ? daisy700 : d700,
                zIndex: 1,
              }}
            />
          )}
        </Box>

        {/* Pre/post line labels — right of bar, collision-avoided */}
        <Box
          sx={{
            position: "relative",
            width: 110,
            height: BAR_H,
            flexShrink: 0,
          }}
        >
          <Box sx={{ position: "absolute", top: preLabelY, left: 0 }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: missingPreCover ? "#9ca3af" : d900,
                lineHeight: 1.2,
              }}
            >
              Pre-grazing
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: missingPreCover ? "#9ca3af" : d900,
                lineHeight: 1.2,
                fontStyle: missingPreCover ? "italic" : "normal",
              }}
            >
              {missingPreCover
                ? "Not recorded"
                : `${metrics.startDryMatter.toLocaleString()} kg DM/ha`}
            </Typography>
          </Box>
          {metrics.dateTimeOut && (
            <Box sx={{ position: "absolute", top: postLabelY, left: 0 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: isOvergrazed ? daisy700 : d700,
                  lineHeight: 1.2,
                }}
              >
                Post-grazing
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: isOvergrazed ? daisy700 : d700,
                  lineHeight: 1.2,
                }}
              >
                {metrics.endDryMatter.toLocaleString()} kg DM/ha
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* end chart group */}

      {/* Stats + actions — flex:1 so buttons reach the right edge */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
        {/* Actions inline at top */}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          {isMachineProposed ? (
            <>
              <Button
                size="small"
                variant="contained"
                onClick={handleAccept}
                disabled={updateReading.isPending || createReading.isPending}
                disableElevation
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  height: 24,
                  minWidth: 0,
                  px: 1.25,
                  backgroundColor: "#2E7831",
                  "&:hover": { backgroundColor: "#1C4321" },
                }}
              >
                Accept
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDraft(metrics);
                  setIsEditing(true);
                }}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  height: 24,
                  minWidth: 0,
                  px: 1.25,
                  borderColor: "#e4e4e7",
                  color: "#374151",
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDelete}
                disabled={updateReading.isPending || createReading.isPending}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  height: 24,
                  minWidth: 0,
                  px: 1.25,
                  borderColor: "#fecaca",
                  color: "#ef4444",
                }}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              {showAcceptedAnimation && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mr: "auto",
                    color: "#15803d",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    Accepted
                  </Typography>
                  <SuccessSparkle />
                </Box>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDraft(metrics);
                  setIsEditing(true);
                }}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  height: 24,
                  minWidth: 0,
                  px: 1.25,
                  borderColor: "#e4e4e7",
                  color: "#374151",
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDelete}
                disabled={deleteReading.isPending || updateReading.isPending}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  height: 24,
                  minWidth: 0,
                  px: 1.25,
                  borderColor: "#fecaca",
                  color: "#ef4444",
                }}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
        {metrics.dateTimeOut ? (
          missingPreCover ? (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                backgroundColor: "#f3f4f6",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                Pre-grazing cover not recorded — grass utilized cannot be
                calculated. Edit this event to add it.
              </Typography>
            </Box>
          ) : (
            <>
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                  }}
                >
                  Grass utilized
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
                >
                  <Typography
                    sx={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: d700,
                      lineHeight: 1.1,
                    }}
                  >
                    {totalKgDM.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                    kg DM
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                  {metrics.fieldArea.toFixed(2)} ha
                </Typography>
              </Box>
              {isOvergrazed && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    backgroundColor: daisy100,
                    borderRadius: "6px",
                    border: `1px solid ${daisy300}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, color: daisy700, fontWeight: 600 }}
                  >
                    ⚠ Overgrazed by {overgraze.toLocaleString()} kg DM
                  </Typography>
                </Box>
              )}
            </>
          )
        ) : grazingInProgress ? (
          <Typography
            sx={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}
          >
            Grazing in progress
          </Typography>
        ) : (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              backgroundColor: "#f3f4f6",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
              Move-out date missing. Edit this event to add it.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Delete button for non-grazing events ────────────────────────────────────
function DeleteEventButton({
  source,
  onDeleted,
}: {
  source: UnifiedEventSource;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);

  const deleteReseed = useDeleteReseedEvent();
  const deleteFeed = useDeleteFeedEvent();
  const deleteFeedPurchase = useDeleteFeedPurchaseEvent();
  const deleteYield = useDeleteYieldEvent();
  const deleteFertilizer = useDeleteFertilizerEvent();
  const deleteFieldPrep = useDeleteFieldPreparationEvent();

  const isPending =
    deleteReseed.isPending ||
    deleteFeed.isPending ||
    deleteFeedPurchase.isPending ||
    deleteYield.isPending ||
    deleteFertilizer.isPending ||
    deleteFieldPrep.isPending;

  function confirm() {
    const cb = {
      onSuccess: () => {
        setOpen(false);
        onDeleted?.();
      },
    };
    switch (source.type) {
      case "reseed":
        deleteReseed.mutate(source.event.guid, cb);
        break;
      case "feed":
        deleteFeed.mutate(source.event.guid, cb);
        break;
      case "feedPurchase":
        deleteFeedPurchase.mutate(source.event.guid, cb);
        break;
      case "yield":
        deleteYield.mutate(source.event.guid, cb);
        break;
      case "fertilizer":
        deleteFertilizer.mutate(source.event.guid, cb);
        break;
      case "fieldPreparation":
        deleteFieldPrep.mutate(source.event.guid, cb);
        break;
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{ fontSize: 12, textTransform: "none" }}
      >
        Delete
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete event?</DialogTitle>
        <DialogContent>
          <DialogContentText>This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={confirm}
            color="error"
            disabled={isPending}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Edit button for non-grazing events ──────────────────────────────────────
function EditEventButton({ source }: { source: UnifiedEventSource }) {
  const [open, setOpen] = useState(false);

  const reseedFieldGuids =
    source.type === "reseed" ? source.event.fieldGuids : undefined;
  const { data: reseedFields = [] } = useFieldsByIds(reseedFieldGuids);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{
          fontSize: 12,
          textTransform: "none",
          borderColor: "#e4e4e7",
          color: "#374151",
        }}
      >
        Edit
      </Button>
      {source.type === "reseed" && (
        <UpdateReseedEvent
          reseedEvent={source.event}
          fields={reseedFields}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
      {source.type === "feed" && (
        <UpdateFeedEvent
          feedEvent={source.event}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
      {source.type === "feedPurchase" && (
        <UpdateFeedPurchaseEvent
          feedPurchaseEvent={source.event}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
      {source.type === "yield" && (
        <UpdateYieldEvent
          yieldEvent={source.event}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
      {source.type === "fertilizer" && (
        <UpdateFertilizerEvent
          fertilizerEvent={source.event}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
      {source.type === "fieldPreparation" && (
        <UpdateFieldPreparationEvent
          fieldPreparationEvent={source.event}
          dialogOpen={open}
          setDialogOpen={setOpen}
        />
      )}
    </>
  );
}

// ─── Single virtualized row (main row + expandable detail) ───────────────────
function EventRowContent({
  ev,
  isExpanded,
  onToggle,
  onDeleted,
  onAccepted,
  showAcceptedAnimation,
}: {
  ev: UnifiedEvent;
  isExpanded: boolean;
  onToggle: () => void;
  onDeleted: () => void;
  onAccepted: (acceptedId: string) => void;
  showAcceptedAnimation: boolean;
}) {
  const isMobile = useIsMobile();
  const isProposed = ev.type === "grazing-proposed";
  return (
    <>
      {/* Main row */}
      <Box
        onClick={onToggle}
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? GRID_COLS_MOBILE : GRID_COLS,
          alignItems: "center",
          cursor: "pointer",
          minHeight: 52,
          backgroundColor: isExpanded ? "#fafafa" : "transparent",
          borderBottom: isExpanded ? "none" : "1px solid #e4e4e7",
          "&:hover": { backgroundColor: "#fafafa" },
        }}
      >
        <Box sx={{ p: 1, pl: 1.5, display: "flex", alignItems: "center" }}>
          <EventTypeIcon type={ev.type} />
        </Box>
        <Box sx={{ ...tdSx, whiteSpace: "nowrap" }}>{ev.date}</Box>
        <Box sx={tdSx}>
          <TypeBadge type={ev.type} />
        </Box>
        <Box sx={{ ...tdSx, overflow: "hidden" }}>
          <Typography noWrap sx={{ fontSize: 13, color: "#374151" }}>
            {ev.fields.join(", ")}
          </Typography>
        </Box>
        {!isMobile && (
          <Box sx={{ ...tdSx, overflow: "hidden" }}>
            <Typography noWrap sx={{ fontSize: 13, color: "#71717a" }}>
              {ev.summary}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            p: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton size="small" sx={{ color: "#9ca3af" }}>
            {isExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Expandable detail */}
      {isExpanded && (
        <Box
          sx={{
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #e4e4e7",
          }}
        >
          {ev.grazingMetrics ? (
            <GrazingDetail
              metrics={ev.grazingMetrics}
              fields={ev.fields}
              onDeleted={onDeleted}
              onAccepted={onAccepted}
              showAcceptedAnimation={showAcceptedAnimation}
            />
          ) : (
            <Box
              sx={{
                px: isMobile ? 2 : 3,
                py: 2,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 1.5 : 4,
                alignItems: isMobile ? "stretch" : "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "4px 16px",
                  flex: 1,
                }}
              >
                {ev.details.map(({ label, value }) => [
                  <Typography
                    key={`lbl-${label}`}
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </Typography>,
                  <Typography
                    key={`val-${label}`}
                    sx={{ fontSize: 13, color: "#374151" }}
                  >
                    {value}
                  </Typography>,
                ])}
                {ev.fields.length > 0 && (
                  <>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}
                    >
                      Fields
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#374151" }}>
                      {ev.fields.join(", ")}
                    </Typography>
                  </>
                )}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <EventPhotos farmEventGuid={ev.id} />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexShrink: 0,
                  justifyContent: isMobile ? "flex-end" : "flex-start",
                }}
              >
                {ev.source && <EditEventButton source={ev.source} />}
                {ev.source && (
                  <DeleteEventButton source={ev.source} onDeleted={onDeleted} />
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

export function MeasuredVirtualRow({
  virtualRow,
  children,
  measureElement,
  depsKey,
}: {
  virtualRow: VirtualItem;
  children: React.ReactNode;
  measureElement: (node: Element | null) => void;
  depsKey: string;
}) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const secondFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const setRowRef = useCallback(
    (node: HTMLDivElement | null) => {
      rowRef.current = node;
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (secondFrameRef.current !== null) {
        window.cancelAnimationFrame(secondFrameRef.current);
        secondFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      measureElement(node);
      if (!node) return;
    },
    [measureElement]
  );

  useLayoutEffect(() => {
    const node = rowRef.current;
    if (!node) return;

    measureElement(node);
    frameRef.current = window.requestAnimationFrame(() => {
      measureElement(node);
      secondFrameRef.current = window.requestAnimationFrame(() => {
        measureElement(node);
      });
      timeoutRef.current = window.setTimeout(() => {
        measureElement(node);
      }, 120);
    });

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (secondFrameRef.current !== null) {
        window.cancelAnimationFrame(secondFrameRef.current);
        secondFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [depsKey, measureElement]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      if (secondFrameRef.current !== null) {
        window.cancelAnimationFrame(secondFrameRef.current);
      }
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return (
    <Box
      ref={setRowRef}
      data-index={virtualRow.index}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Unified virtual list with expandable rows ────────────────────────────────
function UnifiedTable({ events }: { events: UnifiedEvent[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acceptedAnimationId, setAcceptedAnimationId] = useState<string | null>(
    null
  );
  const parentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const eventIdentity = useMemo(
    () => events.map((event) => event.id).join("|"),
    [events]
  );

  const rowVirtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      estimatedUnifiedRowSize(events[index], events[index]?.id === expandedId),
    overscan: 5,
  });

  const handleToggle = useCallback(
    (event: UnifiedEvent, index: number) => {
      const willExpand = expandedId !== event.id;
      rowVirtualizer.resizeItem(
        index,
        estimatedUnifiedRowSize(event, willExpand)
      );
      setExpandedId(willExpand ? event.id : null);
    },
    [expandedId, rowVirtualizer]
  );

  const handleAccepted = useCallback((acceptedId: string) => {
    setExpandedId(acceptedId);
    setAcceptedAnimationId(acceptedId);
  }, []);

  useEffect(() => {
    if (!acceptedAnimationId) return;
    const timeoutId = window.setTimeout(() => {
      setAcceptedAnimationId((id) => (id === acceptedAnimationId ? null : id));
    }, 1800);
    return () => window.clearTimeout(timeoutId);
  }, [acceptedAnimationId]);

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [eventIdentity, rowVirtualizer]);

  return (
    <Box
      sx={{
        border: "1px solid #e4e4e7",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Sticky column header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? GRID_COLS_MOBILE : GRID_COLS,
          alignItems: "center",
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #e4e4e7",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ width: 40, p: 1 }} />
        <Box sx={thSx}>Date</Box>
        <Box sx={thSx}>Type</Box>
        <Box sx={thSx}>Field(s)</Box>
        {!isMobile && <Box sx={thSx}>Summary</Box>}
        <Box sx={{ width: 40 }} />
      </Box>

      {/* Scrollable virtual rows */}
      <Box
        ref={parentRef}
        sx={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}
      >
        {events.length === 0 ? (
          <Box
            sx={{
              py: 6,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#9ca3af" }}>
              No records found.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const ev = events[virtualRow.index];
              const isExpanded = expandedId === ev.id;
              return (
                <MeasuredVirtualRow
                  key={virtualRow.key}
                  virtualRow={virtualRow}
                  measureElement={rowVirtualizer.measureElement}
                  depsKey={`${ev.id}:${isExpanded}:${
                    acceptedAnimationId === ev.id
                  }`}
                >
                  <EventRowContent
                    ev={ev}
                    isExpanded={isExpanded}
                    onToggle={() => handleToggle(ev, virtualRow.index)}
                    onDeleted={() =>
                      setExpandedId((id) => (id === ev.id ? null : id))
                    }
                    onAccepted={handleAccepted}
                    showAcceptedAnimation={acceptedAnimationId === ev.id}
                  />
                </MeasuredVirtualRow>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Export helpers ───────────────────────────────────────────────────────────

function exportCSV(events: UnifiedEvent[]) {
  const blob = new Blob([buildCsvContent(events, TYPE_LABELS)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), {
    href: url,
    download: "ruumi-records.csv",
  });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportPDF(
  events: UnifiedEvent[],
  dateFrom: string,
  dateTo: string,
  groupByField: boolean
) {
  if (events.length === 0) return;

  const content = buildPdfBody(events, TYPE_LABELS, groupByField);

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Ruumi · Records</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;color:#111;margin:0;padding:24px}
  h1{font-size:18px;margin:0 0 4px;color:#1C4321}
  .period{font-size:12px;color:#666;margin:0 0 28px}
  .section{page-break-inside:avoid;margin-bottom:32px}
  .section-title{font-size:14px;font-weight:700;color:#16a34a;border-bottom:2px solid #16a34a;padding-bottom:4px;margin-bottom:8px}
  table{width:100%;border-collapse:collapse;margin-bottom:24px}
  th{text-align:left;font-size:10px;color:#666;text-transform:uppercase;letter-spacing:.05em;padding:5px 8px;border-bottom:1px solid #ccc}
  td{padding:5px 8px;border-bottom:1px solid #eee;vertical-align:top}
  @media print{@page{margin:16mm}}
</style></head><body>
<h1>Ruumi · Records</h1>
<p class="period">Period: ${dateFrom || "—"} to ${dateTo || "—"}${
    groupByField ? " · Grouped by field" : ""
  }</p>
${content}
</body></html>`;

  const w = window.open("", "_blank", "width=800,height=600");
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}

// ─── Records list ─────────────────────────────────────────────────────────────
function RecordsList() {
  const { events: allEvents, allFieldNames } = useUnifiedEvents();
  const isMobile = useIsMobile();

  const [selectedTypes, setSelectedTypes] = useState<UnifiedEvent["type"][]>(
    []
  );
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const [fieldAnchor, setFieldAnchor] = useState<HTMLElement | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  const visibleEvents = useMemo(
    () =>
      filterEvents(allEvents, {
        selectedTypes,
        selectedFields,
        dateFrom,
        dateTo,
      }),
    [allEvents, selectedTypes, selectedFields, dateFrom, dateTo]
  );

  const dateLabel = formatDateRangeLabel(dateFrom, dateTo);
  const isDateActive = Boolean(dateFrom || dateTo);

  const today = new Date();
  const isoToday = today.toISOString().split("T")[0];
  const datePresets = [
    {
      label: "This month",
      from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}-01`,
      to: isoToday,
    },
    {
      label: "Last 90 days",
      from: new Date(today.getTime() - 90 * 86400000)
        .toISOString()
        .split("T")[0],
      to: isoToday,
    },
    {
      label: "This year",
      from: `${today.getFullYear()}-01-01`,
      to: `${today.getFullYear()}-12-31`,
    },
    {
      label: "Last year",
      from: `${today.getFullYear() - 1}-01-01`,
      to: `${today.getFullYear() - 1}-12-31`,
    },
    { label: "All time", from: "", to: "" },
  ];

  const ALL_TYPES: UnifiedEvent["type"][] = [
    "grazing",
    "grazing-proposed",
    "seeding",
    "fertiliser",
    "harvest",
    "feeding",
    "purchase",
    "fieldPrep",
  ];

  const filterBtnSx = {
    textTransform: "none" as const,
    fontSize: 12,
    fontWeight: 500,
    height: 30,
    px: 1.25,
    borderColor: "#e4e4e7",
    color: "#52525b",
    borderRadius: "6px",
    fontFamily: "Inter, sans-serif",
    "&:hover": { borderColor: "#d1d5db", backgroundColor: "#fafafa" },
    // On mobile, the three filters share one row — let each flex to fit.
    ...(isMobile && {
      flex: 1,
      minWidth: 0,
      px: 0.75,
      "& .MuiButton-endIcon": { ml: 0.5 },
      "& .MuiButton-startIcon": { mr: 0.5 },
    }),
  };
  const dashedBtnSx = {
    ...filterBtnSx,
    borderStyle: "dashed" as const,
    color: "#9ca3af",
  };
  const typeChipSx = {
    height: 26,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#f0fdf4",
    border: "1px solid #86efac",
    color: "#16a34a",
    "& .MuiChip-deleteIcon": {
      color: "#86efac",
      "&:hover": { color: "#16a34a" },
    },
  };
  const fieldChipSx = {
    height: 26,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    "& .MuiChip-deleteIcon": {
      color: "#bfdbfe",
      "&:hover": { color: "#1d4ed8" },
    },
  };
  const popoverPaper = { elevation: 2, sx: { borderRadius: "10px", mt: 0.5 } };
  const popoverAnchor = {
    vertical: "bottom" as const,
    horizontal: "left" as const,
  };
  const popoverTransform = {
    vertical: "top" as const,
    horizontal: "left" as const,
  };

  const checkboxSx = {
    p: 0,
    color: "#d1d5db",
    "&.Mui-checked": { color: "#16a34a" },
  };

  const toggleType = (type: UnifiedEvent["type"]) =>
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );

  const toggleField = (f: string) =>
    setSelectedFields((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e4e4e7",
          borderRadius: "10px",
          p: isMobile ? "8px 10px" : "8px 14px",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 0.75 : 1,
          flexWrap: isMobile ? "nowrap" : "wrap",
        }}
      >
        {/* Type chips + picker */}
        {selectedTypes.map((type) => (
          <Chip
            key={type}
            label={TYPE_LABELS[type]}
            size="small"
            onDelete={() =>
              setSelectedTypes(selectedTypes.filter((t) => t !== type))
            }
            sx={typeChipSx}
          />
        ))}
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => setTypeAnchor(e.currentTarget)}
          endIcon={
            selectedTypes.length === 0 ? (
              <ExpandMoreIcon sx={{ fontSize: 13 }} />
            ) : undefined
          }
          sx={selectedTypes.length > 0 ? dashedBtnSx : filterBtnSx}
        >
          {selectedTypes.length === 0 ? "All types" : "+ Type"}
        </Button>
        <Popover
          open={Boolean(typeAnchor)}
          anchorEl={typeAnchor}
          onClose={() => setTypeAnchor(null)}
          anchorOrigin={popoverAnchor}
          transformOrigin={popoverTransform}
          PaperProps={popoverPaper}
        >
          <Box sx={{ py: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                px: 2,
                py: 0.5,
              }}
            >
              Event type
            </Typography>
            {ALL_TYPES.map((type) => (
              <MenuItem
                key={type}
                dense
                onClick={() => toggleType(type)}
                sx={{ gap: 1, px: 2 }}
              >
                <Checkbox
                  size="small"
                  checked={selectedTypes.includes(type)}
                  sx={checkboxSx}
                />
                <ListItemText
                  primary={TYPE_LABELS[type]}
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {!isMobile && (
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        )}

        {/* Date range button + popover */}
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => setDateAnchor(e.currentTarget)}
          startIcon={<CalendarMonthIcon sx={{ fontSize: 13 }} />}
          endIcon={<ExpandMoreIcon sx={{ fontSize: 13 }} />}
          sx={
            isDateActive
              ? {
                  ...filterBtnSx,
                  backgroundColor: "#fffbeb",
                  borderColor: "#fde68a",
                  color: "#92400e",
                  "&:hover": {
                    backgroundColor: "#fef9c3",
                    borderColor: "#fde68a",
                  },
                }
              : filterBtnSx
          }
        >
          {dateLabel}
        </Button>
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={popoverAnchor}
          transformOrigin={popoverTransform}
          PaperProps={{
            ...popoverPaper,
            sx: { ...popoverPaper.sx, p: 2, minWidth: 270 },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 1,
            }}
          >
            Date range
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <TextField
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiInputBase-input": { fontSize: 12, py: "5px" },
              }}
            />
            <Typography sx={{ color: "#d1d5db" }}>–</Typography>
            <TextField
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiInputBase-input": { fontSize: 12, py: "5px" },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {datePresets.map((p) => (
              <Button
                key={p.label}
                size="small"
                variant="outlined"
                onClick={() => {
                  setDateFrom(p.from);
                  setDateTo(p.to);
                }}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  borderColor: "#e4e4e7",
                  color: "#374151",
                  px: 1,
                  py: 0.25,
                  minWidth: 0,
                }}
              >
                {p.label}
              </Button>
            ))}
          </Box>
        </Popover>

        {!isMobile && (
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        )}

        {/* Field chips + picker */}
        {selectedFields.map((f) => (
          <Chip
            key={f}
            label={f}
            size="small"
            onDelete={() =>
              setSelectedFields(selectedFields.filter((x) => x !== f))
            }
            sx={fieldChipSx}
          />
        ))}
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => setFieldAnchor(e.currentTarget)}
          endIcon={
            selectedFields.length === 0 ? (
              <ExpandMoreIcon sx={{ fontSize: 13 }} />
            ) : undefined
          }
          sx={selectedFields.length > 0 ? dashedBtnSx : filterBtnSx}
        >
          {selectedFields.length === 0 ? "All fields" : "+ Field"}
        </Button>
        <Popover
          open={Boolean(fieldAnchor)}
          anchorEl={fieldAnchor}
          onClose={() => setFieldAnchor(null)}
          anchorOrigin={popoverAnchor}
          transformOrigin={popoverTransform}
          PaperProps={popoverPaper}
        >
          <Box sx={{ py: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                px: 2,
                py: 0.5,
              }}
            >
              Fields
            </Typography>
            {allFieldNames.map((f) => (
              <MenuItem
                key={f}
                dense
                onClick={() => toggleField(f)}
                sx={{ gap: 1, px: 2 }}
              >
                <Checkbox
                  size="small"
                  checked={selectedFields.includes(f)}
                  sx={checkboxSx}
                />
                <ListItemText
                  primary={f}
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {!isMobile && <Box sx={{ flex: 1 }} />}

        {/* Export dropdown — hidden on mobile (exporting is a desktop task) */}
        {!isMobile && (
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon sx={{ width: 15, height: 15 }} />}
            endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={exportBtnSx}
          >
            Export
          </Button>
        )}
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5, minWidth: 210 },
          }}
        >
          <MenuItem
            dense
            onClick={() => {
              exportCSV(visibleEvents);
              setExportAnchor(null);
            }}
            sx={{ fontSize: 13, display: "flex", gap: 1.5, py: 1 }}
          >
            <FileDownloadIcon
              sx={{ fontSize: 16, color: "#71717a", flexShrink: 0 }}
            />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                Export CSV
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                {visibleEvents.length} record
                {visibleEvents.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            dense
            onClick={() => {
              exportPDF(visibleEvents, dateFrom, dateTo, false);
              setExportAnchor(null);
            }}
            sx={{ fontSize: 13, display: "flex", gap: 1.5, py: 1 }}
          >
            <FileDownloadIcon
              sx={{ fontSize: 16, color: "#71717a", flexShrink: 0 }}
            />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                Export PDF
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                Chronological
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem
            dense
            onClick={() => {
              exportPDF(visibleEvents, dateFrom, dateTo, true);
              setExportAnchor(null);
            }}
            sx={{ fontSize: 13, display: "flex", gap: 1.5, py: 1 }}
          >
            <FileDownloadIcon
              sx={{ fontSize: 16, color: "#71717a", flexShrink: 0 }}
            />
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                Export PDF · by field
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                One section per field
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Paper>

      <UnifiedTable events={visibleEvents} />
    </Box>
  );
}

export default function RecordsPage() {
  return (
    <Container maxWidth={false} disableGutters sx={{ flexGrow: 1 }}>
      <Box sx={{ paddingTop: "env(safe-area-inset-top)" }} />
      <RecordsList />
    </Container>
  );
}
